// ── Shared UI Components ──────────────────────────────────────────────

// ── useNarrow — responsive breakpoint hook ───────────────────────────
function useNarrow(breakpoint = 900) {
  const [narrow, setNarrow] = React.useState(typeof window !== 'undefined' ? window.innerWidth < breakpoint : false);
  React.useEffect(() => {
    const onResize = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return narrow;
}
window.useNarrow = useNarrow;

// ── TopBar ────────────────────────────────────────────────────────────
function AppTopBar({ title, onMenu, onSignOut, isAdmin, onAdmin, onAskGQ }) {
  return (
    <header style={{
      position:"sticky", top:0, zIndex:100, height:56,
      display:"flex", alignItems:"center", justifyContent:"space-between",
      padding:"0 20px", background:"rgba(8,8,8,0.92)",
      backdropFilter:"blur(16px)", borderBottom:"1px solid var(--border)",
    }}>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        <button onClick={onMenu} style={{ background:"none", border:"none", color:"var(--muted)", cursor:"pointer", padding:4, display:"flex" }}>
          <Menu size={18}/>
        </button>
        <span style={{ fontFamily:"var(--font-ui)", fontSize:14, fontWeight:600, color:"var(--ink)", letterSpacing:"-0.01em" }}>{title}</span>
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:10 }}>
        {onAskGQ && (
          <button onClick={onAskGQ} style={{
            display:"inline-flex", alignItems:"center", gap:6,
            background:"rgba(217,70,239,0.10)", border:"1px solid rgba(217,70,239,0.30)",
            borderRadius:"var(--r-full)", padding:"4px 10px", cursor:"pointer",
            fontFamily:"var(--font-ui)", fontSize:12, color:"#D946EF", fontWeight:500,
          }}>
            <span style={{ fontFamily:"'Inter Tight', 'Inter', sans-serif", fontWeight:800, letterSpacing:"-0.02em" }}>GQ</span> Ask AI
          </button>
        )}
        <div style={{ display:"flex", alignItems:"center", gap:6, padding:"3px 10px", borderRadius:"var(--r-full)", background:"var(--green-dim)", border:"1px solid rgba(17,255,153,0.2)" }}>
          <div style={{ width:6, height:6, borderRadius:"50%", background:"var(--green)", boxShadow:"0 0 6px rgba(17,255,153,0.6)" }}/>
          <span style={{ fontFamily:"var(--font-mono)", fontSize:11, color:"var(--green)" }}>Markets open</span>
        </div>
        {isAdmin && (
          <button onClick={onAdmin} title="Admin dashboard" style={{ background:"none", border:"none", color:"#D946EF", cursor:"pointer", padding:4, display:"flex" }}>
            <Shield size={16}/>
          </button>
        )}
        <button onClick={onSignOut} style={{ background:"none", border:"none", color:"var(--muted)", cursor:"pointer", padding:4, display:"flex" }}>
          <LogOut size={16}/>
        </button>
      </div>
    </header>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────────
const NAV_ITEMS = [
  { id:"vault",       label:"My strategy vault",   icon:Wallet  },
  { id:"designer",    label:"Strategy lab",         icon:Layers  },
  { id:"optimize",    label:"GQ™ Optimize",         icon:Cpu     },
  { id:"stress",      label:"Stress test",          icon:Shield  },
  { id:"marketplace", label:"Marketplace",          icon:Store   },
  { id:"exchanges",   label:"Exchanges",            icon:DollarSign },
  { id:"settings",    label:"Settings",             icon:Settings},
];

function AppSidebar({ open, active, onNavigate, onClose, isAdmin }) {
  return (
    <>
      {open && <div onClick={onClose} style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", backdropFilter:"blur(4px)", zIndex:200 }}/>}
      <aside style={{
        position:"fixed", top:0, left:0, bottom:0, zIndex:300, width:248,
        background:"#000", borderRight:"1px solid var(--border)",
        transform: open ? "translateX(0)" : "translateX(-100%)",
        transition:"transform 0.22s cubic-bezier(0.4,0,0.2,1)",
        display:"flex", flexDirection:"column", overflowY:"auto",
      }}>
        {/* Logo */}
        <div style={{ padding:"18px 18px 14px", display:"flex", alignItems:"center", justifyContent:"space-between", borderBottom:"1px solid var(--border)" }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ width:32, height:32, borderRadius:8, background:"var(--primary-dim)", border:"1px solid rgba(217,70,239,0.3)", display:"flex", alignItems:"center", justifyContent:"center" }}>
              <span style={{ fontFamily:"'Inter Tight', 'Inter', sans-serif", fontSize:14, fontWeight:800, color:"var(--primary)", letterSpacing:"-0.02em" }}>GQ</span>
            </div>
            <div>
              <div style={{ fontFamily:"var(--font-ui)", fontSize:13, fontWeight:700, color:"var(--ink)", letterSpacing:"-0.01em" }}>Genetic Quant</div>
              <div style={{ fontFamily:"var(--font-mono)", fontSize:10, color:"var(--subtle)" }}>.ai members</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:"var(--subtle)", cursor:"pointer", padding:2, display:"flex" }}><X size={15}/></button>
        </div>
        {/* Nav */}
        <div style={{ flex:1, padding:"10px 10px 16px" }}>
          {NAV_ITEMS.map(item => {
            if (item.id === 'exchanges' && !isAdmin) return null;
            const Ic = item.icon;
            const act = active === item.id;
            return (
              <button key={item.id} onClick={() => onNavigate(item.id)} style={{
                width:"100%", display:"flex", alignItems:"center", gap:10,
                padding:"9px 10px", borderRadius:8, border:"none", cursor:"pointer",
                background: act ? "var(--primary-dim)" : "transparent",
                color: act ? "var(--primary)" : "var(--muted)",
                fontFamily:"var(--font-ui)", fontSize:13, fontWeight: act ? 600 : 400,
                textAlign:"left", marginBottom:2, transition:"all 0.15s",
              }}>
                <Ic size={14} color={act ? "var(--primary)" : undefined}/>
                {item.label}
                {act && <div style={{ marginLeft:"auto", width:4, height:4, borderRadius:"50%", background:"var(--green)" }}/>}
              </button>
            );
          })}
          {isAdmin && (
            <button onClick={() => onNavigate("admin")} style={{
              width:"100%", display:"flex", alignItems:"center", gap:10,
              padding:"9px 10px", borderRadius:8, border:"none", cursor:"pointer",
              background: active==="admin" ? "rgba(255,32,71,0.1)" : "transparent",
              color: active==="admin" ? "var(--red)" : "var(--muted)",
              fontFamily:"var(--font-ui)", fontSize:13, fontWeight:500, textAlign:"left", marginBottom:2,
            }}>
              <Shield size={14}/> Admin Dashboard
            </button>
          )}
        </div>
        <div style={{ padding:"12px 16px 20px", borderTop:"1px solid var(--border)" }}>
          <a href="../index.html" style={{ display:"flex", alignItems:"center", gap:8, fontFamily:"var(--font-ui)", fontSize:12, color:"var(--subtle)", textDecoration:"none" }}>
            <ChevronRight size={12} style={{ transform:"rotate(180deg)" }}/> Back to website
          </a>
          <div style={{ marginTop:14, paddingTop:12, borderTop:"1px solid var(--border)", display:"flex", gap:14, fontFamily:"var(--font-ui)", fontSize:10, color:"var(--subtle)" }}>
            <a href="disclaimer.html" style={{color:"inherit",textDecoration:"none"}}>Disclaimer</a>
            <a href="../../terms.html"   style={{color:"inherit",textDecoration:"none"}}>Terms</a>
            <a href="../../privacy.html" style={{color:"inherit",textDecoration:"none"}}>Privacy</a>
          </div>
        </div>
      </aside>
    </>
  );
}

// ── StatCard ──────────────────────────────────────────────────────────
function StatCard({ title, value, sub, icon: Ic, accent = "default" }) {
  const a = {
    green:  { ib:"var(--green-dim)",   ic:"var(--green)"   },
    purple: { ib:"var(--primary-dim)", ic:"var(--primary)" },
    cyan:   { ib:"var(--accent-dim)",  ic:"var(--accent)"  },
    red:    { ib:"var(--red-dim)",     ic:"var(--red)"     },
    default:{ ib:"rgba(255,255,255,0.05)", ic:"var(--muted)" },
  }[accent];
  return (
    <div className="card" style={{ display:"flex", flexDirection:"column", gap:10 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <span style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--muted)" }}>{title}</span>
        {Ic && <div style={{ width:28, height:28, borderRadius:8, background:a.ib, display:"flex", alignItems:"center", justifyContent:"center" }}><Ic size={13} color={a.ic}/></div>}
      </div>
      <div style={{ fontFamily:"var(--font-mono)", fontSize:20, fontWeight:600, color:"var(--ink)", letterSpacing:"-0.02em" }}>{value}</div>
      {sub && <div style={{ fontFamily:"var(--font-ui)", fontSize:11, color:"var(--muted)" }}>{sub}</div>}
    </div>
  );
}

// ── Mini sparkline ────────────────────────────────────────────────────
function Sparkline({ data, color="#11ff99", height=48 }) {
  const w=220, h=height, min=Math.min(...data), max=Math.max(...data);
  const rx=i=>(i/(data.length-1))*w;
  const ry=v=>h-((v-min)/(max-min||1))*(h-6)-3;
  const line=data.map((v,i)=>`${i===0?"M":"L"}${rx(i)},${ry(v)}`).join(" ");
  const area=`${line} L${w},${h} L0,${h} Z`;
  return (
    <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display:"block" }}>
      <defs>
        <linearGradient id={`sg_${color.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.22"/>
          <stop offset="100%" stopColor={color} stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={area} fill={`url(#sg_${color.replace("#","")})`}/>
      <path d={line} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  );
}

// ── Toast hook ────────────────────────────────────────────────────────
function useToast() {
  const [toast, setToast] = React.useState(null);
  const fire = (msg, sub, dur=2400) => {
    setToast({ msg, sub });
    setTimeout(() => setToast(null), dur);
  };
  const ToastEl = toast ? (
    <div className="toast">
      <div style={{ fontSize:13, fontWeight:500 }}>{toast.msg}</div>
      {toast.sub && <div style={{ fontSize:12, color:"var(--muted)", marginTop:2 }}>{toast.sub}</div>}
    </div>
  ) : null;
  return [ToastEl, fire];
}

// ── Page header ───────────────────────────────────────────────────────
function PageHdr({ icon: Ic, iconColor="var(--primary)", iconBg="var(--primary-dim)", title, subtitle, action }) {
  return (
    <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", flexWrap:"wrap", gap:12, marginBottom:22 }}>
      <div style={{ display:"flex", alignItems:"center", gap:12 }}>
        {Ic && <div style={{ width:40, height:40, borderRadius:10, background:iconBg, display:"flex", alignItems:"center", justifyContent:"center" }}><Ic size={18} color={iconColor}/></div>}
        <div>
          <h1 style={{ fontFamily:"var(--font-ui)", fontSize:18, fontWeight:700, color:"var(--ink)", letterSpacing:"-0.015em", margin:0 }}>{title}</h1>
          {subtitle && <div style={{ fontFamily:"var(--font-ui)", fontSize:12, color:"var(--muted)", marginTop:2 }}>{subtitle}</div>}
        </div>
      </div>
      {action}
    </div>
  );
}

// ── Generic stub page ─────────────────────────────────────────────────
function StubPage({ title, icon: Ic, desc }) {
  return (
    <div>
      <PageHdr icon={Ic} title={title} subtitle={desc}/>
      <div className="card" style={{ minHeight:260, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:12 }}>
        {Ic && <Ic size={32} color="var(--border-strong)"/>}
        <div style={{ fontFamily:"var(--font-ui)", fontSize:13, color:"var(--subtle)" }}>Full implementation coming soon.</div>
      </div>
    </div>
  );
}

// ── EmptyState ────────────────────────────────────────────────────────
// Editorial first-empty surface. Use when a list, table or workspace has
// zero items because the user hasn't done the thing yet.
function EmptyState({ icon: Ic, title, body, primary, secondary, compact = false }) {
  return (
    <div style={{
      padding: compact ? "32px 24px" : "56px 32px",
      textAlign: "center",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
    }}>
      {Ic && (
        <div style={{
          width: compact ? 40 : 56, height: compact ? 40 : 56,
          borderRadius: "var(--r-lg)",
          background: "rgba(255,255,255,0.025)",
          border: "1px solid var(--border)",
          display: "flex", alignItems: "center", justifyContent: "center",
          marginBottom: 4,
        }}>
          <Ic size={compact ? 18 : 22} color="var(--muted)" strokeWidth={1.4}/>
        </div>
      )}
      <div style={{
        fontFamily: "var(--font-serif)",
        fontSize: compact ? 20 : 26, fontWeight: 500,
        color: "var(--ink)", letterSpacing: "-0.025em", lineHeight: 1.1,
        maxWidth: 380, textWrap: "balance",
      }}>{title}</div>
      {body && (
        <div style={{
          fontFamily: "var(--font-ui)",
          fontSize: 13, color: "var(--muted)", lineHeight: 1.55,
          maxWidth: 360, textWrap: "pretty",
        }}>{body}</div>
      )}
      {(primary || secondary) && (
        <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap", justifyContent: "center" }}>
          {primary && (
            <button className="btn btn-primary btn-sm" onClick={primary.onClick}>
              {primary.icon && <primary.icon size={12}/>} {primary.label}
            </button>
          )}
          {secondary && (
            <button className="btn btn-ghost btn-sm" onClick={secondary.onClick}>
              {secondary.icon && <secondary.icon size={12}/>} {secondary.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── ErrorState ────────────────────────────────────────────────────────
// For thrown / network / data errors. Quieter than empty — the user did
// nothing wrong, the system did.
function ErrorState({ title = "Something went sideways.", body, onRetry, compact = false, detail }) {
  const [showDetail, setShowDetail] = React.useState(false);
  return (
    <div style={{
      padding: compact ? "28px 22px" : "44px 32px",
      textAlign: "center",
      display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
    }}>
      <div style={{
        width: 40, height: 40, borderRadius: "var(--r-lg)",
        background: "var(--red-dim)", border: "1px solid rgba(248,113,113,0.20)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <AlertTriangle size={18} color="var(--red)" strokeWidth={1.5}/>
      </div>
      <div style={{
        fontFamily: "var(--font-serif)", fontSize: 22, fontWeight: 500,
        color: "var(--ink)", letterSpacing: "-0.02em", lineHeight: 1.15,
        maxWidth: 380,
      }}>{title}</div>
      {body && (
        <div style={{ fontFamily: "var(--font-ui)", fontSize: 13, color: "var(--muted)", maxWidth: 380, lineHeight: 1.55 }}>{body}</div>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap", justifyContent: "center" }}>
        {onRetry && (
          <button className="btn btn-ghost btn-sm" onClick={onRetry}>
            <RefreshCw size={11}/> Try again
          </button>
        )}
        {detail && (
          <button className="btn btn-ghost btn-sm" onClick={() => setShowDetail(d => !d)}>
            {showDetail ? "Hide" : "Show"} detail
          </button>
        )}
      </div>
      {showDetail && detail && (
        <pre style={{
          fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--muted)",
          background: "var(--bg-deep)", border: "1px solid var(--border)",
          borderRadius: "var(--r-md)", padding: 12, marginTop: 4,
          maxWidth: 520, textAlign: "left", whiteSpace: "pre-wrap", wordBreak: "break-all",
        }}>{detail}</pre>
      )}
    </div>
  );
}

// ── Skeleton primitives ───────────────────────────────────────────────
// Subtle pulsing blocks. Use during the first paint of any async surface.
function Skeleton({ width = "100%", height = 14, radius = 6, style = {} }) {
  return <div style={{
    width, height, borderRadius: radius,
    background: "linear-gradient(90deg, rgba(255,255,255,0.04) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.04) 100%)",
    backgroundSize: "200% 100%",
    animation: "skelShimmer 1.4s ease-in-out infinite",
    ...style,
  }}/>;
}

function SkeletonRow({ lines = 2 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8, padding: "14px 0" }}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton key={i} height={i === 0 ? 14 : 11} width={i === 0 ? "60%" : "85%"}/>
      ))}
    </div>
  );
}

function SkeletonCard({ height = 92 }) {
  return (
    <div style={{
      background: "var(--bg-card)", border: "1px solid var(--border)",
      borderRadius: "var(--r-lg)", padding: 16,
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <Skeleton width="40%" height={11}/>
      <Skeleton width="65%" height={18}/>
      <Skeleton width="30%" height={10}/>
      {height > 92 && <Skeleton width="100%" height={height - 70} style={{ marginTop: 4 }}/>}
    </div>
  );
}

function SkeletonList({ rows = 4 }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{
          background: "var(--bg-card)", border: "1px solid var(--border)",
          borderRadius: "var(--r-lg)", padding: 16,
          display: "flex", alignItems: "center", gap: 16,
        }}>
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
            <Skeleton width="35%" height={14}/>
            <Skeleton width="70%" height={11}/>
          </div>
          <Skeleton width={90} height={32} radius={8}/>
        </div>
      ))}
    </div>
  );
}

// ── Spinner ───────────────────────────────────────────────────────────
function Spinner({ size = 14, color = "var(--primary)" }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: "50%",
      border: `2px solid ${color === "var(--primary)" ? "rgba(217,70,239,0.25)" : "rgba(255,255,255,0.15)"}`,
      borderTopColor: color,
      animation: "spin 0.7s linear infinite",
      display: "inline-block", verticalAlign: "middle",
    }}/>
  );
}

// ── InlineLoader — slim labelled loading row ─────────────────────────
function InlineLoader({ label = "Loading…" }) {
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10, padding: "20px 0",
      justifyContent: "center", color: "var(--muted)",
      fontFamily: "var(--font-ui)", fontSize: 13,
    }}>
      <Spinner size={12} color="var(--muted)"/>
      {label}
    </div>
  );
}

// ── PageState — wraps the common load → error → empty → content flow ──
function PageState({ loading, error, empty, onRetry, skeleton, emptyState, children }) {
  if (loading) return skeleton || <InlineLoader/>;
  if (error)   return <ErrorState body={typeof error === "string" ? error : error?.message} detail={error?.stack} onRetry={onRetry}/>;
  if (empty)   return emptyState || <EmptyState title="Nothing here yet." body="Once you create something, it'll show up here."/>;
  return children;
}

// ── InlineError — slim inline error row with optional retry ──────────
function InlineError({ error, onRetry, label = "Failed to load" }) {
  if (!error) return null;
  return (
    <div style={{
      background: "rgba(255,32,71,0.06)", border: "1px solid rgba(255,32,71,0.25)",
      borderRadius: "var(--r-md)", padding: "10px 14px",
      display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
      fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--red)",
    }}>
      <span><b>{label}.</b> {error.message || String(error)}</span>
      {onRetry && (
        <button className="btn btn-ghost btn-sm" style={{fontSize:11, padding:"3px 10px"}} onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}

// Inject the shared keyframes once.
if (typeof document !== "undefined" && !document.getElementById("__gq_ui_keyframes")) {
  const st = document.createElement("style");
  st.id = "__gq_ui_keyframes";
  st.textContent = `
    @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
    @keyframes skelShimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
    @keyframes gqFadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
  `;
  document.head.appendChild(st);
}

Object.assign(window, {
  AppTopBar, AppSidebar, StatCard, Sparkline, useToast, PageHdr, StubPage,
  EmptyState, ErrorState, Skeleton, SkeletonRow, SkeletonCard, SkeletonList,
  Spinner, InlineLoader, InlineError, PageState, useNarrow,
});
